import React from 'react';
import {
  METRIC_TRANSLATION,
  MetricKey,
  formatMetricValue,
} from '../../config/metricTranslation';
import HelpPopover from './HelpPopover';

interface MetricLabelProps {
  metric: MetricKey;
  value?: number;
  size?: 'sm' | 'md' | 'lg';
  align?: 'left' | 'center';
  /**
   * Optional help text — when provided, renders a click-to-open `?` next
   * to the label so the team can see the platform-native source for the
   * metric. For Snapshot totals, used to surface the "totals are
   * approximate, definitions differ per platform" warning.
   */
  tooltip?: string;
}

const SIZE_TOKENS: Record<NonNullable<MetricLabelProps['size']>, { social: number; paid: number; value: number }> = {
  sm: { social: 11, paid: 10, value: 16 },
  md: { social: 13, paid: 11, value: 24 },
  lg: { social: 14, paid: 12, value: 32 },
};

const MetricLabel: React.FC<MetricLabelProps> = ({
  metric,
  value,
  size = 'md',
  align = 'left',
  tooltip,
}) => {
  const entry = METRIC_TRANSLATION[metric];
  const tokens = SIZE_TOKENS[size];
  const wrap: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: align === 'center' ? 'center' : 'flex-start',
    gap: '2px',
    textAlign: align,
  };
  const socialStyle: React.CSSProperties = {
    fontSize: tokens.social,
    fontWeight: 600,
    color: 'var(--fg-1)',
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    // Inline-flex when a help icon needs alignment; bare span otherwise.
    ...(tooltip ? { display: 'inline-flex', alignItems: 'center', gap: 4 } : {}),
  };
  return (
    <div style={wrap}>
      <span style={socialStyle}>
        {entry.social}
        {tooltip && <HelpPopover content={tooltip} size={tokens.social} />}
      </span>
      <span
        style={{
          fontSize: tokens.paid,
          fontWeight: 400,
          color: 'var(--fg-3)',
          letterSpacing: '0.01em',
        }}
      >
        {entry.paid}
      </span>
      {value !== undefined && (
        <span
          style={{
            marginTop: 8,
            fontSize: tokens.value,
            fontWeight: 600,
            color: 'var(--fg-1)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatMetricValue(metric, value)}
        </span>
      )}
    </div>
  );
};

export default MetricLabel;
